import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Container,
  Paper,
  Skeleton,
} from '@mui/material';
import {
  ArrowBack,
  Twitter,
  Language,
  Email,
  Send,
  Chat,
  OpenInNew,
} from '@mui/icons-material';
import { useNFT } from '../hooks/useNFT';
import { ipfsUriToUrl } from '../utils/ipfs';
import { getKYCContractAddress } from '../config/contracts';
import { useWeb3React } from '@web3-react/core';


interface ProfileData {
  name?: string;
  email?: string;
  description?: string;
  social?: {
    twitter?: string;
    discord?: string;
    telegram?: string;
  };
  website?: string;
  image?: string;
  banner?: string;
}

interface ProfilePageData {
  tokenId: string;
  profile: ProfileData | null;
  loading: boolean;
  error: string | null;
}

const AssetsDetailPage: React.FC = () => {
  const { tokenId } = useParams<{ tokenId: string }>();
  const { chainId } = useWeb3React();
  const { loadNFTMetadata } = useNFT();
  const [profileData, setProfileData] = useState<ProfilePageData>({
    tokenId: tokenId || '',
    profile: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    if (tokenId && chainId) {
      loadProfileData();
    }
  }, [tokenId, chainId]);

  const loadProfileData = async () => {
    if (!tokenId || !chainId) {
      setProfileData(prev => ({
        ...prev,
        loading: false,
        error: 'Missing necessary parameters'
      }));
      return;
    }

    try {
      setProfileData(prev => ({ ...prev, loading: true, error: null }));
      
      const contractAddress = getKYCContractAddress(chainId);
      if (!contractAddress) {
        throw new Error('Unsupported network');
      }

      const metadata = await loadNFTMetadata(contractAddress, tokenId);
      
      if (!metadata) {
        throw new Error('Unable to fetch NFT data');
      }

     // console.log("metadata:",metadata);

      // Parse profile information from metadata
      const profileInfo: ProfileData = {
        name: metadata.name,
        email: (metadata as any).metadata.email,
        description: metadata.description,
        social: (metadata as any).metadata.social,
        website: (metadata as any).metadata.website,
        image: (metadata as any).metadata.image,
        banner: (metadata as any).metadata.banner
      };

      setProfileData({
        tokenId,
        profile: profileInfo,
        loading: false,
        error: null
      });
    } catch (error: any) {
      console.error('Failed to load profile:', error);
      setProfileData(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load profile'
      }));
    }
  };



  // Social media icon mapping
  const getSocialIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter':
        return <Twitter />;
      case 'discord':
        return <Chat />;
      case 'telegram':
        return <Send />;
      default:
        return <Language />;
    }
  };

  // Get social media link
  const getSocialLink = (platform: string, username: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter':
        return `https://twitter.com/${username}`;
      case 'discord':
        return `https://discord.com/users/${username}`;
      case 'telegram':
        return `https://t.me/${username}`;
      default:
        return username;
    }
  };

  // Loading state
  if (profileData.loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          component={Link}
          to="/assets"
          startIcon={<ArrowBack />}
          sx={{ mb: 3 }}
        >
          Back to List
        </Button>
        
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {/* Banner skeleton */}
          <Skeleton variant="rectangular" height={200} />
          
          <Box sx={{ p: 3 }}>
            {/* Avatar skeleton */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: -8 }}>
              <Skeleton variant="circular" width={120} height={120} sx={{ mr: 3 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={200} height={40} />
                <Skeleton variant="text" width={300} height={20} />
              </Box>
            </Box>
            
            {/* Content skeleton */}
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width={200} height={40} />
          </Box>
        </Paper>
      </Container>
    );
  }

  // Error state
  if (profileData.error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          component={Link}
          to="/assets"
          startIcon={<ArrowBack />}
          sx={{ mb: 3 }}
        >
          Back to List
        </Button>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {profileData.error}
        </Alert>
      </Container>
    );
  }

  // No data state
  if (!profileData.profile) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          component={Link}
          to="/assets"
          startIcon={<ArrowBack />}
          sx={{ mb: 3 }}
        >
          Back to List
        </Button>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Profile does not exist or is not public yet
        </Alert>
      </Container>
    );
  }

  const { profile } = profileData;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        component={Link}
        to="/assets"
        startIcon={<ArrowBack />}
        sx={{ mb: 3 }}
      >
        Back to List
      </Button>

      {/* Profile Card */}
      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        {/* Banner Image */}
        <Box
          sx={{
            height: 200,
            background: profile.banner 
              ? `url(${ipfsUriToUrl(profile.banner)}) center/cover`
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            position: 'relative'
          }}
        />
        
        {/* Profile Info Area */}
        <Box sx={{ p: 3 }}>
          {/* Avatar and Basic Info */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3, mt: -8 }}>
            <Avatar
              src={profile.image ? ipfsUriToUrl(profile.image) : undefined}
              sx={{
                width: 120,
                height: 120,
                mr: 3,
                border: '4px solid white',
                boxShadow: 2,
                fontSize: '2rem'
              }}
            >
              {profile.name?.charAt(0)?.toUpperCase() || '?'}
            </Avatar>
            
            <Box sx={{ flex: 1, mt: 8 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                {profile.name || `User #${tokenId}`}
              </Typography>
              
              {profile.description && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {profile.description}
                </Typography>
              )}
              
              {/* Social Media Links */}
              {profile.social && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {Object.entries(profile.social).map(([platform, username]) => {
                    if (!username) return null;
                    return (
                      <IconButton
                        key={platform}
                        component="a"
                        href={getSocialLink(platform, username)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark'
                          }
                        }}
                        size="small"
                      >
                        {getSocialIcon(platform)}
                      </IconButton>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          {/* Contact Information */}
          <Grid container spacing={3}>
            {profile.email && (
              <Grid item xs={12} sm={6}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Email color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        {profile.email}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            
            {profile.website && (
              <Grid item xs={12} sm={6}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Language color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Website
                      </Typography>
                      <Button
                        component="a"
                        href={profile.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="text"
                        size="small"
                        endIcon={<OpenInNew />}
                        sx={{ p: 0, justifyContent: 'flex-start' }}
                      >
                        {profile.website}
                      </Button>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
          </Grid>
          
          {/* Social Media Details */}
          {profile.social && Object.keys(profile.social).length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Social Media
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(profile.social).map(([platform, username]) => {
                  if (!username) return null;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={platform}>
                      <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {getSocialIcon(platform)}
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                              {platform}
                            </Typography>
                            <Button
                              component="a"
                              href={getSocialLink(platform, username)}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="text"
                              size="small"
                              sx={{ p: 0, justifyContent: 'flex-start' }}
                            >
                              @{username}
                            </Button>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>
      
      {/* KYC Info Card */}
      <Paper elevation={0} sx={{ borderRadius: 3, p: 3 }}>
        <Typography variant="h6" gutterBottom>
         KYC Info
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Token ID:
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
            #{tokenId}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default AssetsDetailPage;